import type { ComponentProps, FC } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { Favicon } from "../../ui/layout/favicon";

const CampaignContent = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colorsPalettes.carbon[25]};
    border-radius: 8px;
    padding: 4px 8px 4px 4px;
    gap: 8px;
    cursor: pointer;
    z-index: 2;
`;

const CampaignIconContainer = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: ${colorsPalettes.carbon[0]};
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colorsPalettes.carbon[50]};
`;

export const ComboboxStyled = styled(Combobox)`
    && {
        z-index: 1054;
    }
`;

const CampaignInfo = (props: ComponentProps<typeof Stack>) => <Stack {...props} />;

const CampaignTitle = ({ fontVariant = "chip1TopRow", ...props }: Partial<ComponentProps<typeof Text>>) => (
    <Text
        fontVariant={fontVariant}
        {...props}
    />
);

const CampaignSubtitle = ({ fontVariant = "queryBar1", ...props }: Partial<ComponentProps<typeof Text>>) => (
    <Text
        fontVariant={fontVariant}
        {...props}
    />
);

export const MagicBackground = styled.div<{ $active: boolean }>`
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    display: ${({ $active }) => ($active ? "block" : "none")};
    background: ${rgba(colorsPalettes.carbon["500"], 0.7)};
    z-index: 1;
`;

export const CampaignEmptyState: FC<{ ref?: React.Ref<HTMLDivElement> }> = ({ ref }) => {
    const translate = useTranslation();
    return (
        <CampaignContent ref={ref}>
            <CampaignIconContainer>
                <SWReactIcons
                    size="xs"
                    iconName="segment-folder"
                />
            </CampaignIconContainer>
            <CampaignInfo>
                <CampaignTitle>
                    {translate("monitor_and_protect.component.campaign_select.empty_state.title")}
                </CampaignTitle>
                <CampaignSubtitle>
                    {translate("monitor_and_protect.component.campaign_select.empty_state.subtitle")}
                </CampaignSubtitle>
            </CampaignInfo>
            <SWReactIcons
                size="12px"
                iconName="arrow"
                iconColor={colorsPalettes.carbon[200]}
            />
        </CampaignContent>
    );
};

export interface ICampaignSelectorData {
    id: string;
    name: string;
    domain: string;
    keywordsCount: number;
}

export const CampaignSelectedState: FC<{
    campaign: ICampaignSelectorData;
    ref?: React.Ref<HTMLDivElement>;
}> = ({ campaign, ref }) => {
    const translate = useTranslation();
    return (
        <CampaignContent ref={ref}>
            <CampaignIconContainer>
                <Favicon
                    iconUrl={getFaviconUrl(campaign.domain)}
                    alt={campaign.domain}
                    size={32}
                />
            </CampaignIconContainer>
            <CampaignInfo>
                <CampaignTitle>{campaign.name}</CampaignTitle>
                <CampaignSubtitle>
                    {translate("monitor_and_protect.component.campaign_select.selected.caption", {
                        keywords_count: campaign.keywordsCount,
                    })}
                </CampaignSubtitle>
            </CampaignInfo>
            <SWReactIcons
                size="12px"
                iconName="arrow"
                iconColor={colorsPalettes.carbon[200]}
            />
        </CampaignContent>
    );
};
